import { intl, ui } from '@beelday/common'
import { FC } from 'react'
import styled from '@emotion/styled'
import { Colors } from 'common/presentation/colors'
import { translate } from 'intl/translate'

type BannerTone = 'info' | 'warning' | 'error'

type Props = {
	actionLabel?: string
	detail?: string
	message: string
	onAction?: () => Promise<void> | void
	onDismiss?: () => void
	tone?: BannerTone
	topOffset?: number
}

const getBackground = (tone: BannerTone): string => {
	switch (tone) {
		case 'warning':
			return 'linear-gradient(160deg, #f8a530 0%, #e07a12 100%)'
		case 'error':
			return 'linear-gradient(160deg, #d85a4b 0%, #b83525 100%)'
		default:
			return Colors.white
	}
}

const getBoxShadow = (tone: BannerTone): string => {
	switch (tone) {
		case 'warning':
			return '0 6px 32px rgba(220, 120, 10, 0.38)'
		case 'error':
			return '0 6px 32px rgba(180, 40, 30, 0.38)'
		default:
			return '0 4px 20px rgba(0, 0, 0, 0.12)'
	}
}

const getTextColor = (tone: BannerTone): string => {
	return tone === 'info' ? Colors.indigoBlue : Colors.white
}

const Container = styled.div<{ tone: BannerTone; topOffset: number }>`
	background: ${({ tone }) => getBackground(tone)};
	box-shadow: ${({ tone }) => getBoxShadow(tone)};
	position: fixed;
	top: ${({ topOffset }) => `${topOffset}px`};
	left: 0;
	right: 0;
	margin: 0 auto;
	padding: 12px 40px 16px 40px;
	/* Below confirm banners (10000): a confirmation the trainer just asked for
	   must never be covered by a notice ("end group work? Yes" was). */
	z-index: 9999;
	border-radius: 0 0 16px 16px;
	display: flex;
	flex-direction: column;
	gap: 10px;

	${ui.responsive.smallTablet} {
		left: 50%;
		right: auto;
		transform: translateX(-50%);
		width: 36vw;
		padding: 12px 24px 20px 24px;
		border-radius: 0 0 20px 20px;
	}
`

const MessageRow = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
`

const Text = styled.div<{ tone: BannerTone }>`
	color: ${({ tone }) => getTextColor(tone)};
	font-family: Ubuntu;
	font-size: 14px;
	font-weight: 500;
	text-align: center;

	${ui.responsive.smallTablet} {
		font-size: 18px;
	}
`

const Detail = styled.div<{ tone: BannerTone }>`
	color: ${({ tone }) => getTextColor(tone)};
	font-family: Ubuntu;
	font-size: 14px;
	text-align: center;
	opacity: 0.85;
`

const DismissButton = styled.button`
	position: absolute;
	top: 8px;
	right: 10px;
	background: none;
	border: none;
	cursor: pointer;
	padding: 4px;
	display: flex;
	align-items: center;
	justify-content: center;
	opacity: 0.7;
	&:hover {
		opacity: 1;
	}
`

const ActionButton = styled.button<{ tone: BannerTone }>`
	align-self: center;
	background: ${({ tone }) =>
		tone === 'info' ? Colors.indigoBlue : Colors.white};
	border: none;
	border-radius: 999px;
	color: ${({ tone }) => (tone === 'info' ? Colors.white : Colors.indigoBlue)};
	cursor: pointer;
	font-family: Ubuntu;
	font-size: 14px;
	font-weight: 700;
	padding: 10px 18px;
`

const WarningIcon: FC = () => (
	<svg
		width="20"
		height="20"
		viewBox="0 0 20 20"
		fill="none"
		aria-hidden="true"
		style={{ flexShrink: 0 }}
	>
		<path
			d="M10 3L18 17H2L10 3Z"
			stroke="white"
			strokeWidth="1.5"
			strokeLinejoin="round"
			fill="none"
		/>
		<path
			d="M10 9v3.5"
			stroke="white"
			strokeWidth="1.5"
			strokeLinecap="round"
		/>
		<circle cx="10" cy="14.5" r="0.75" fill="white" />
	</svg>
)

const ErrorIcon: FC = () => (
	<svg
		width="20"
		height="20"
		viewBox="0 0 20 20"
		fill="none"
		aria-hidden="true"
		style={{ flexShrink: 0 }}
	>
		<circle
			cx="10"
			cy="10"
			r="8"
			stroke="white"
			strokeWidth="1.5"
			fill="none"
		/>
		<path
			d="M7 7l6 6M13 7l-6 6"
			stroke="white"
			strokeWidth="1.5"
			strokeLinecap="round"
		/>
	</svg>
)

const ToneIcon: FC<{ tone: BannerTone }> = ({ tone }) => {
	if (tone === 'warning') return <WarningIcon />
	if (tone === 'error') return <ErrorIcon />
	return null
}

const CloseIcon: FC<{ tone: BannerTone }> = ({ tone }) => {
	const color = tone === 'info' ? Colors.indigoBlue : 'white'
	return (
		<svg
			width="16"
			height="16"
			viewBox="0 0 16 16"
			fill="none"
			aria-hidden="true"
		>
			<path
				d="M3 3l10 10M13 3L3 13"
				stroke={color}
				strokeWidth="1.8"
				strokeLinecap="round"
			/>
		</svg>
	)
}

const OfflineFriendlyInfoBanner: FC<Props> = ({
	actionLabel,
	detail,
	message,
	onAction,
	onDismiss,
	tone = 'info',
	topOffset = 0,
}) => {
	const closeLabel = intl.useTranslate()('close')
	return (
		<Container tone={tone} topOffset={topOffset}>
			{onDismiss ? (
				<DismissButton onClick={onDismiss} aria-label={closeLabel}>
					<CloseIcon tone={tone} />
				</DismissButton>
			) : null}
			<MessageRow>
				<ToneIcon tone={tone} />
				<Text tone={tone}>{translate(message)}</Text>
			</MessageRow>
			{detail ? <Detail tone={tone}>{detail}</Detail> : null}
			{actionLabel && onAction ? (
				<ActionButton
					tone={tone}
					onClick={() => {
						void onAction()
					}}
				>
					{translate(actionLabel)}
				</ActionButton>
			) : null}
		</Container>
	)
}

export default OfflineFriendlyInfoBanner
